import { Box } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CATEGORY_PRODUCTS_PAGE_SIZE,
  useCategory,
} from "../../hooks/queries/useCategory";
import type { CategoryDetailParams } from "../../services/productPlaneApi";
import type { EntityDetailProps } from "../../types/entityRegistry";
import type { MainEntity } from "../../types/entityTypes";
import { ProductCard } from "../../types/productPlane";
import DetailSections, { DetailSection } from "../Detail/DetailSections";
import { EntityNavigationProvider } from "../Detail/EntityLink";
import { SourceJumpProvider } from "../Detail/sourceJump";
import EntityFunctionalHeader from "../Layout/EntityDetailOverlay/EntityFunctionalHeader";
import { EntityDetailStatus } from "../Layout/EntityDetailStatus";
import CategoryHead, { UNLABELLED_CATEGORY } from "./CategoryHead";
import { categorySections } from "./categorySections";

// Width (px) the view keeps to on a page of its own
const STANDALONE_WIDTH = 1200;

// The most the backend gives in one page of a list
const MOST_PER_PAGE = 200;

// The pages the backend gives of its own accord
const NO_PAGING: CategoryDetailParams = {};

// What a view built on the category view adds to it: the class view of the
// value chain adds its chain sections and a line under the head
export interface CategoryDetailViewProps extends EntityDetailProps {
  // The type the header names; the category's own by default
  entityType?: "category" | "class";
  // Shown under the head; the head then leaves out its own step into the
  // value chain
  headExtra?: React.ReactNode;
  // Shown before the products
  extraSections?: DetailSection[];
}

/**
 * The detail of a category: the registry's Detail component of the type.
 *
 * It fetches the class by its id and shows the overlay's header, the head,
 * and the sections under a strip that stays in view. A list the response
 * holds a page of is asked for again, larger or at another place, when the
 * user asks for the rest.
 */
const CategoryDetailView: React.FC<CategoryDetailViewProps> = ({
  entityId,
  mode = "overlay",
  onEntityClick,
  onClose,
  scrollContainerRef,
  entityType = "category",
  headExtra,
  extraSections,
}) => {
  // The pages asked for: "Show all" raises a limit, the product list turns
  // They belong to the class they were asked for: another class starts with
  // the pages the backend gives
  const [asked, setAsked] = useState<{
    entityId: string;
    paging: CategoryDetailParams;
  }>({ entityId, paging: {} });
  const paging = asked.entityId === entityId ? asked.paging : NO_PAGING;
  const setPaging = useCallback(
    (change: (current: CategoryDetailParams) => CategoryDetailParams) =>
      setAsked((current) => ({
        entityId,
        paging: change(current.entityId === entityId ? current.paging : {}),
      })),
    [entityId],
  );
  const { category, loading, error } = useCategory(entityId || null, paging);

  const ownScrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollContainerRef ?? ownScrollContainerRef;

  const ownSections = useMemo(
    () =>
      category
        ? categorySections(category, mode, {
            showAllCapabilities: () =>
              setPaging((current) => ({
                ...current,
                capabilities_limit: Math.min(
                  category.capabilities.total,
                  MOST_PER_PAGE,
                ),
              })),
            showAllSpecifications: () =>
              setPaging((current) => ({
                ...current,
                specifications_limit: Math.min(
                  category.specifications.total,
                  MOST_PER_PAGE,
                ),
              })),
            pageProducts: (offset) =>
              setPaging((current) => ({
                ...current,
                products_limit: CATEGORY_PRODUCTS_PAGE_SIZE,
                products_offset: offset,
              })),
            openProduct: (product: ProductCard) =>
              onEntityClick?.(product.id, product.type),
          })
        : [],
    [category, mode, onEntityClick, setPaging],
  );

  // The added sections go before the products
  const sections = useMemo(() => {
    if (!extraSections || extraSections.length === 0) {
      return ownSections;
    }
    const at = ownSections.findIndex((section) => section.id === "products");
    const before = at < 0 ? ownSections.length : at;
    return [
      ...ownSections.slice(0, before),
      ...extraSections,
      ...ownSections.slice(before),
    ];
  }, [ownSections, extraSections]);

  // What the header reads: it shows the type and opens the full page
  const mainEntity = useMemo<MainEntity | null>(
    () =>
      category
        ? {
            id: category.id,
            name: category.label ?? UNLABELLED_CATEGORY,
            entityType,
          }
        : null,
    [category, entityType],
  );

  // Another class starts at its top
  const categoryId = category?.id;
  useEffect(() => {
    if (categoryId && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [categoryId, scrollRef]);

  if (loading || error) {
    return <EntityDetailStatus loading={loading} error={error} mode={mode} />;
  }

  if (!category || !mainEntity) {
    return null;
  }

  return (
    <EntityNavigationProvider onEntityClick={onEntityClick}>
      <SourceJumpProvider>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EntityFunctionalHeader
            mainEntity={mainEntity}
            onClose={onClose || (() => {})}
            mode={mode}
          />
          <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Box
              sx={{
                maxWidth: mode === "standalone" ? STANDALONE_WIDTH : "100%",
                mx: "auto",
              }}
            >
              {/* A view that adds a line under the head brings its own entry
                  point to the value chain */}
              <CategoryHead category={category} valueChainLink={!headExtra} />
              {headExtra && <Box sx={{ px: 3, pb: 2 }}>{headExtra}</Box>}
              <DetailSections
                sections={sections}
                scrollContainerRef={scrollRef}
              />
            </Box>
          </Box>
        </Box>
      </SourceJumpProvider>
    </EntityNavigationProvider>
  );
};

export default CategoryDetailView;
